import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomerProfile from "../../callsModule/common/TabProfile";
import CustomerDataset from "../../callsModule/common/TabDataset";
import CustomerNotes from "../../callsModule/common/TabNotes";
import Payments from "../payments/Payments";
import CustomerHistory from "../../callsModule/common/TabHistory";
import CustomerCallsHistory from "../../callsModule/common/TabCallsHistory";
import CustomerChats from "../../callsModule/common/TabChats";
import CustomerTexts from "../../callsModule/common/TabTexts";
import CustomerEmails from "../../callsModule/common/TabEmails";
import { getcustomerprofilebyguid } from "../../../services/customerinfoModule/profileServices";
import SendEmail from "../../callsModule/common/SendEmail";
import SendText from "../../callsModule/common/SendText";
import AddRelatedRecord from "../../callsModule/common/AddRelatedRecord";
import PaymentConfirmModal from "../payments/PaymentConfirmModal";
import { PAYMENT_STRINGS } from "../../../common/constants/customerModule/paymentConstants";
import CustomerOptIn from "../../callsModule/common/TabOptin";
import { CsvExportProvider } from "./CsvExportContext.jsx";
import ExportButtons from "./ExportButtons.jsx";
//import TabConversation from "../../callsModule/common/TabConversation";

// NOTE: Calls / Chats / Emails are hidden, not removed — the entries and their
// components stay wired up here. Delete `hidden: true` from an entry to bring
// that tab back.
const tabConfig = (customerguid) => [
  //{ label: "Conversation", value: "Conversation", component: <TabConversation customerguid={customerguid} />     },
  {
    label: "Profile",
    value: "Profile",
    component: (
      <CustomerProfile agentType={"Customer"} customerguid={customerguid} />
    ),
  },
  {
    label: "Dataset",
    value: "Dataset",
    component: <CustomerDataset customerguid={customerguid} />,
  },
  {
    label: "Notes",
    value: "Notes",
    component: <CustomerNotes customerguid={customerguid} />,
  },
  {
    label: "Payments",
    value: "Payments",
    component: <Payments customerguid={customerguid} />,
  },
  {
    label: "History",
    value: "History",
    component: <CustomerHistory customerguid={customerguid} />,
  },
  {
    label: "Calls",
    value: "Calls",
    hidden: true,
    component: <CustomerCallsHistory customerguid={customerguid} />,
  },
  {
    label: "Chats",
    value: "Chats",
    hidden: true,
    component: <CustomerChats customerguid={customerguid} />,
  },
  {
    label: "Texts",
    value: "Texts",
    component: <CustomerTexts customerguid={customerguid} />,
  },
  {
    label: "Emails",
    value: "Emails",
    hidden: true,
    component: <CustomerEmails customerguid={customerguid} />,
  },
  {
    // Label-only rename — component/file (TabOptin.jsx) is unchanged.
    label: "OptOut",
    value: "OptOut",
    component: <CustomerOptIn customerguid={customerguid} />,
  },
];

const CustomerInfo = ({ customerguid, onBack, menuActions = [] }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState("Profile");
  const [localCustomerGuid, setLocalCustomerGuid] = useState(customerguid);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const tabs = tabConfig(customerguid);
  // Hidden entries never reach the Tabs strip or the content area.
  const visibleTabs = tabs.filter((tab) => !tab.hidden);
  const renderTabContent = () => {
    const current = visibleTabs.find((tab) => tab.value === currentTab);
    return current?.component || null;
  };
  useEffect(() => {
    setLocalCustomerGuid(customerguid);
  }, [customerguid]);

  useEffect(() => {
    let mounted = true;
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const data = await getcustomerprofilebyguid({ customerguid: localCustomerGuid });
        if (mounted) setProfile(data?.[0] || null);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (localCustomerGuid) {
      fetchProfile();
    }

    return () => {
      mounted = false;
    };
  }, [localCustomerGuid]);
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalMode, setPaymentModalMode] = useState('now');
  const openPaymentModal = (mode) => {
    setPaymentModalMode(mode);
    setPaymentModalOpen(true);
    handleMenuClose();
  };
  if (!customerguid) return null;

  // CSV files come out as: customer_<id>_<tab>_<YYYYMMDD>.csv
  const filePrefix = `customer_${profile?.customerid ?? ""}`;

  return (
    <CsvExportProvider filePrefix={filePrefix}>
      <Box sx={{ p: 2 }}>
        {!loading && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                gap: 2,
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <IconButton onClick={onBack} sx={{ mr: 1 }} aria-label="back">
                  <ArrowCircleLeftOutlinedIcon />
                </IconButton>
                <Typography variant="h6" noWrap>
                  {profile && `${profile.firstname} ${profile.lastname}`}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ExportButtons customerguid={localCustomerGuid} profile={profile} />
                <IconButton onClick={handleMenuClick} aria-label="menu">
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    sx={{ cursor: "pointer", fontSize: "14px" }}
                    onClick={undefined}
                  >
                    {<SendText customerguid={customerguid} />}
                  </MenuItem>
                  <MenuItem
                    sx={{ cursor: "pointer", fontSize: "14px" }}
                    onClick={undefined}
                  >
                    {<SendEmail customerguid={customerguid} />}
                  </MenuItem>
                  <MenuItem
                    sx={{ cursor: "pointer", fontSize: "14px" }}
                    onClick={undefined}
                  >
                    {<AddRelatedRecord customerguid={customerguid} />}
                  </MenuItem>
                  <MenuItem sx={{ cursor: "pointer", fontSize: "14px" }} onClick={() => openPaymentModal('now')}>
                    {PAYMENT_STRINGS.TAKE_PAYMENT_NOW}
                  </MenuItem>
                  <MenuItem sx={{ cursor: "pointer", fontSize: "14px" }} onClick={() => openPaymentModal('schedule')}>
                    {PAYMENT_STRINGS.SCHEDULE_PAYMENT_TITLE}
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
            <Tabs
              value={currentTab}
              onChange={(e, val) => setCurrentTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              {visibleTabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </>
        )}
        <Box>{renderTabContent()}</Box>
        {paymentModalOpen && (
          <PaymentConfirmModal
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            selectedGuid={localCustomerGuid}
            mode={paymentModalMode}
          />
        )}
      </Box>
    </CsvExportProvider>
  );
};

export default CustomerInfo;